/**
 * Forecasts Page — Фаза B (Редизайн с группировкой по тематикам)
 *
 * Прогнозы сгруппированы по 7 тематическим разделам:
 * 1. ⏰ Временные (daily, weekly, monthly, yearly)
 * 2. 💼 Сфера жизни (business, love, family, health)
 * 3. 🪐 Астрологические (transits, progressions, solar, lunar, eclipses)
 * 4. 🔮 Специальные (karmic, custom, electional)
 * 5. 📊 Нумерологические (personal year/month/day, lucky dates)
 * 6. 💑 Совместимость (synastry, numerology compatibility)
 * 7. 🎯 Кармические (karmic nodes, past lives, destiny)
 *
 * Каждая группа — свёртываемая секция с карточками в grid.
 * Карточки показывают бейдж периодичности и статус кэша.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccessBadge, UpgradePrompt } from '../components/access';
import { TIER_ORDER } from '../data/featureAccess';
import { SubscriptionTier, getRequiredTier } from '../../shared/types/access';
import api, { type ApiResponse } from '../api';
import ForecastCard from '../components/ForecastCard';
import CustomForecastForm from '../components/CustomForecastForm';
import CacheIndicator from '../components/CacheIndicator';
import { setMetaTags, resetMetaTags, buildCanonicalUrl, PAGE_SEO } from '../utils/seo';
import { renderMarkdown } from '../utils/renderMarkdown';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import AnimatedLoadingState from '../components/AnimatedLoadingState';
import LocationWizard from '../components/LocationWizard';
import LocationBanner from '../components/LocationBanner';

// ============================================================
// Types
// ============================================================

type ForecastId = string;

interface ForecastCardDef {
  id: ForecastId;
  featureId?: string;    // ID функции для проверки доступа (по умолчанию = id)
  icon: string;
  title: string;
  description: string;
  gradient: string;
  endpoint?: string;       // API endpoint (если есть)
  method?: 'GET' | 'POST'; // HTTP метод
  navigateTo?: string;     // Навигация вместо API (electional)
}

interface ForecastCategory {
  id: string;
  icon: string;
  title: string;
  description: string;
  color: string;
  forecasts: ForecastCardDef[];
}

interface ForecastData {
  forecast?: string;
  answer?: string;
  days?: Array<{ date: string; summary: string }>;
  highlights?: string[];
  recommendations?: string[];
  warnings?: string[];
  type?: string;
  title?: string;
  rating?: number;
  tone?: string;
  summary?: string;
  aiInterpretation?: string;
  generatedAt?: string;
  cached?: boolean;
  text?: string;
  keyTransits?: string[];
}

// ============================================================
// Registry — все прогнозы сгруппированы по тематикам
// ============================================================

const FORECAST_CATEGORIES: ForecastCategory[] = [
  {
    id: 'temporal',
    icon: '⏰',
    title: 'Временные прогнозы',
    description: 'Прогнозы на разные периоды — от дня до года',
    color: '#F4E4A6',
    forecasts: [
      { id: 'daily', icon: '🌟', title: 'Ежедневный', description: 'Транзиты и нумерология дня. Ключевые влияния и рекомендации на сегодня.', gradient: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 100%)', method: 'POST' },
      { id: 'weekly', icon: '📆', title: 'Еженедельный', description: 'Прогноз на 7 дней с ключевыми событиями и рекомендациями.', gradient: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)', endpoint: '/forecasts/weekly', method: 'GET' },
      { id: 'monthly', icon: '🗓️', title: 'Ежемесячный', description: 'Прогноз на 30 дней: ретроградные планеты, фазы Луны, важные периоды.', gradient: 'linear-gradient(135deg, #2196F3 0%, #1565C0 100%)', endpoint: '/forecasts/monthly', method: 'GET' },
      { id: 'yearly', icon: '📅', title: 'Годовой', description: 'Подробный анализ года по месяцам — ключевые темы и события.', gradient: 'linear-gradient(135deg, #FF5722 0%, #D84315 100%)', method: 'POST' },
    ],
  },
  {
    id: 'life-spheres',
    icon: '💼',
    title: 'Сфера жизни',
    description: 'Прогнозы по ключевым областям жизни',
    color: '#4CAF50',
    forecasts: [
      { id: 'business', icon: '💼', title: 'Бизнес', description: 'Карьера и финансы: влияние 10-го и 2-го домов на работу и доход.', gradient: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)', endpoint: '/forecasts/business', method: 'GET' },
      { id: 'love', icon: '❤️', title: 'Любовь', description: 'Прогноз отношений: Венера, Марс и 7-й дом в фокусе.', gradient: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)', endpoint: '/forecasts/love', method: 'GET' },
      { id: 'family', icon: '🏠', title: 'Семья', description: 'Семейный прогноз: 4-й дом, Луна и домашние дела.', gradient: 'linear-gradient(135deg, #795548 0%, #5D4037 100%)', endpoint: '/forecasts/family', method: 'GET' },
      { id: 'health', icon: '🏥', title: 'Здоровье', description: 'Прогноз здоровья: 6-й дом, Хирон и уязвимые места.', gradient: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)', method: 'POST' },
    ],
  },
  {
    id: 'astrological',
    icon: '🪐',
    title: 'Астрологические методы',
    description: 'Транзиты, прогрессии, возвраты планет',
    color: '#9B59B6',
    forecasts: [
      { id: 'transits', icon: '📊', title: 'Транзиты', description: 'Текущие влияния планет на вашу натальную карту.', gradient: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)', method: 'POST' },
      { id: 'progressions', icon: '📈', title: 'Прогрессии', description: 'Символические дирекции и вторичные прогрессии.', gradient: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)', method: 'POST' },
      { id: 'solar', icon: '☀️', title: 'Соляр', description: 'Годовой прогноз на день рождения — ключевые темы года.', gradient: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)', method: 'POST' },
      { id: 'lunar', icon: '🌙', title: 'Лунар', description: 'Месячный прогноз по Луне — эмоциональные циклы.', gradient: 'linear-gradient(135deg, #607D8B 0%, #455A64 100%)', method: 'POST' },
      { id: 'lunar-returns', icon: '🔄', title: 'Лунные возвраты', description: 'Ежемесячные лунные циклы и их влияние.', gradient: 'linear-gradient(135deg, #CDDC39 0%, #AFB42B 100%)', method: 'POST' },
      { id: 'eclipses', icon: '🌑', title: 'Затмения', description: 'Влияние солнечных и лунных затмений на вашу жизнь.', gradient: 'linear-gradient(135deg, #2C3E50 0%, #1A252F 100%)', method: 'POST' },
    ],
  },
  {
    id: 'numerology',
    icon: '🔢',
    title: 'Нумерологические прогнозы',
    description: 'Персональные циклы и счастливые даты',
    color: '#3498DB',
    forecasts: [
      { id: 'numerology-year', icon: '🔢', title: 'Персональный год', description: 'Нумерологический прогноз на текущий год.', gradient: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)', method: 'POST' },
      { id: 'numerology-month', icon: '📊', title: 'Персональный месяц', description: 'Нумерологический прогноз на текущий месяц.', gradient: 'linear-gradient(135deg, #5DADE2 0%, #3498DB 100%)', method: 'POST' },
      { id: 'numerology-day', icon: '🌟', title: 'Персональный день', description: 'Нумерологический прогноз на сегодня.', gradient: 'linear-gradient(135deg, #85C1E9 0%, #5DADE2 100%)', method: 'POST' },
      { id: 'lucky-dates', icon: '🍀', title: 'Счастливые даты', description: 'Лучшие даты для важных дел в этом месяце.', gradient: 'linear-gradient(135deg, #2ECC71 0%, #27AE60 100%)', method: 'POST' },
    ],
  },
  {
    id: 'karmic',
    icon: '🐉',
    title: 'Кармические прогнозы',
    description: 'Кармические узлы, прошлые жизни, предназначение',
    color: '#673AB7',
    forecasts: [
      { id: 'karmic', icon: '🐉', title: 'Кармические узлы', description: 'Раху и Кету в натальной карте — кармические задачи.', gradient: 'linear-gradient(135deg, #673AB7 0%, #512DA8 100%)', method: 'POST' },
      { id: 'past-lives', icon: '🔮', title: 'Прошлые жизни', description: 'Кармические уроки и связи из прошлых воплощений.', gradient: 'linear-gradient(135deg, #8E44AD 0%, #6C3483 100%)', method: 'POST' },
      { id: 'destiny-path', icon: '⭐', title: 'Путь предназначения', description: 'Ваша миссия и ключевые точки жизненного пути.', gradient: 'linear-gradient(135deg, #F39C12 0%, #D68910 100%)', method: 'POST' },
    ],
  },
  {
    id: 'special',
    icon: '✨',
    title: 'Специальные',
    description: 'Кастомные вопросы и выбор даты',
    color: '#009688',
    forecasts: [
      { id: 'custom', icon: '💬', title: 'Кастомный вопрос', description: 'Задайте свой вопрос — ответ будет рассчитан на основе вашей натальной карты.', gradient: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)' },
      { id: 'electional', icon: '📅', title: 'Выбор даты', description: 'Элективная астрология для выбора благоприятного времени.', gradient: 'linear-gradient(135deg, #009688 0%, #00796B 100%)', navigateTo: '/electional' },
    ],
  },
];

// GET-эндпоинты (имеют собственный роут)
const GET_ENDPOINTS: ForecastId[] = ['weekly', 'monthly', 'business', 'love', 'family'];

// ============================================================
// Styles
// ============================================================

const pageStyle: React.CSSProperties = {
  minHeight: '80vh',
  padding: '32px 20px',
  maxWidth: '1200px',
  margin: '0 auto',
  position: 'relative',
};

const titleStyle: React.CSSProperties = {
  fontSize: 'clamp(28px, 5vw, 40px)',
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
  backgroundSize: '200% auto',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  marginBottom: '8px',
  textAlign: 'center',
  animation: 'goldShimmer 3s ease-in-out infinite',
};

const subtitleStyle: React.CSSProperties = {
  color: '#b0b0b0',
  fontSize: '16px',
  textAlign: 'center',
  marginBottom: '40px',
  maxWidth: '600px',
  margin: '0 auto 40px',
  lineHeight: 1.6,
};

const goldAccentLine: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: '10%',
  right: '10%',
  height: '2px',
  background: 'linear-gradient(90deg, transparent, #d4af37, transparent)',
};

const categorySectionStyle: React.CSSProperties = {
  marginBottom: '32px',
};

const categoryHeaderStyle = (color: string): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
  cursor: 'pointer',
  padding: '12px 16px',
  background: 'rgba(13, 20, 40, 0.85)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  border: '1px solid rgba(212, 175, 55, 0.12)',
  boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
  transition: 'all 0.2s ease',
});

const categoryIconStyle = (color: string): React.CSSProperties => ({
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${color}44 0%, ${color}22 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '24px',
  flexShrink: 0,
});

const categoryTitleStyle: React.CSSProperties = {
  color: '#f0f0f0',
  fontSize: '18px',
  fontFamily: "'Cormorant Garamond', 'Times New Roman', serif",
  fontStyle: 'italic',
  fontWeight: 500,
  flex: 1,
};

const categoryDescStyle: React.CSSProperties = {
  color: '#b0b0b0',
  fontSize: '13px',
  fontFamily: 'var(--font-sans)',
};

const gridContainerStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  gap: '16px',
};

const cardStyle: React.CSSProperties = {
  background: 'rgba(13, 20, 40, 0.85)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(212, 175, 55, 0.12)',
  borderRadius: '24px',
  boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
  padding: '24px',
  marginBottom: '20px',
};

const backBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid rgba(212, 175, 55, 0.3)',
  borderRadius: '8px',
  padding: '8px 16px',
  color: '#d4af37',
  fontSize: '14px',
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
};

// ============================================================
// Component
// ============================================================

const ForecastsPage: React.FC = () => {
  const navigate = useNavigate();

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // State
  const [loading, setLoading] = useState<ForecastId | null>(null);
  const [forecastData, setForecastData] = useState<ForecastData | null>(null);
  const [activeForecastId, setActiveForecastId] = useState<ForecastId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCached, setIsCached] = useState(false);

  // Custom forecast state
  const [customResult, setCustomResult] = useState<{ text: string; question: string } | null>(null);
  const [customError, setCustomError] = useState<string | null>(null);
  const [showCustomForm, setShowCustomForm] = useState(false);

  // Collapsed categories
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});

  // Subscription status
  const [userTier, setUserTier] = useState<SubscriptionTier>('free');
  const [loadingTier, setLoadingTier] = useState(true);
  
  // Upgrade prompt
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [pendingFeatureId, setPendingFeatureId] = useState<string | null>(null);
  const [pendingRequiredTier, setPendingRequiredTier] = useState<SubscriptionTier>('free');

  // Current location for accurate forecasts
  const [currentCity, setCurrentCity] = useState<string | null>(null);
  const [showLocationWizard, setShowLocationWizard] = useState(false);
  const [locationLoaded, setLocationLoaded] = useState(false);

  // Load current location from profile
  useEffect(() => {
    const loadLocation = async () => {
      try {
        const res = await api.get<{ current_city: string | null; current_latitude: number | null }>(
          '/profile'
        );
        if (res.success && res.data) {
          setCurrentCity(res.data.current_city || null);
        }
      } catch {
        // Не критично
      } finally {
        setLocationLoaded(true);
      }
    };
    loadLocation();
  }, []);

  // Load subscription status
  useEffect(() => {
    const fetchTier = async () => {
      try {
        const API_URL = import.meta.env.VITE_API_URL || '';
        const res = await fetch(`${API_URL}/api/payment/status`, {
          credentials: 'include', // HttpOnly cookies (фаза 1.1)
        });
        const data = await res.json();
        if (data.success && data.data?.subscription) {
          const tier = data.data.subscription.tier || 'free';
          setUserTier(tier);
        } else {
          setUserTier('free');
        }
      } catch {
        setUserTier('free');
      } finally {
        setLoadingTier(false);
      }
    };
    fetchTier();
  }, []);

  // SEO
  useEffect(() => {
    setMetaTags({
      ...PAGE_SEO.forecasts,
      canonicalUrl: buildCanonicalUrl('/forecasts'),
    });
    return () => { resetMetaTags(); };
  }, []);

  // Toggle category collapse
  const toggleCategory = (categoryId: string) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // Fetch a forecast by ID
  const fetchForecast = useCallback(async (id: ForecastId) => {
    // Find card definition
    let cardDef: ForecastCardDef | undefined;
    for (const cat of FORECAST_CATEGORIES) {
      cardDef = cat.forecasts.find(f => f.id === id);
      if (cardDef) break;
    }

    // Navigation-based forecasts
    if (cardDef?.navigateTo) {
      navigate(cardDef.navigateTo);
      return;
    }

    // Custom forecast
    if (id === 'custom') {
      setShowCustomForm(true);
      setActiveForecastId('custom');
      setForecastData(null);
      setError(null);
      setCustomResult(null);
      setCustomError(null);
      return;
    }

    setLoading(id);
    setError(null);
    setForecastData(null);
    setActiveForecastId(id);
    setShowCustomForm(false);
    setCustomResult(null);
    setCustomError(null);
    setIsCached(false);

    try {
      let res: ApiResponse<ForecastData>;

      // Все прогнозы теперь идут через единый endpoint
      const endpoint = cardDef?.endpoint || '/forecast/execute';
      const method = cardDef?.method || 'POST';

      if (method === 'GET' && cardDef?.endpoint) {
        res = await api.get<ForecastData>(endpoint);
      } else {
        res = await api.post<ForecastData>(endpoint, {
          type: id,
          forceRefresh: false,
        });
      }

      // Обновляем состояние кэша
      setIsCached(Boolean(res.cached));

      if (res.success && res.data) {
        // Сервер возвращает { success, data: { success, data: { forecast }, cached, generatedAt } }
        const wrapper = res.data as any;
        const data = wrapper.data || wrapper;

        if (data.forecast !== undefined || data.answer !== undefined) {
          setForecastData(data);
        } else if (data.aiInterpretation || data.summary || data.text) {
          setForecastData({
            forecast: data.aiInterpretation || data.summary || data.text || '',
            days: data.days,
            highlights: data.highlights || data.keyTransits,
            recommendations: data.recommendations,
            warnings: data.warnings,
            type: data.type || id,
            title: data.title || cardDef?.title || 'Прогноз',
            rating: data.rating,
            tone: data.tone,
            summary: data.summary,
            aiInterpretation: data.aiInterpretation,
            generatedAt: data.generatedAt || new Date().toISOString(),
            cached: data.cached,
          });
        } else if (data.text) {
          setForecastData({ forecast: data.text, type: id, title: cardDef?.title });
        }
      } else {
        setError(res.error || 'Не удалось загрузить прогноз');
      }
    } catch {
      setError('Ошибка соединения с сервером');
    } finally {
      setLoading(null);
    }
  }, [navigate]);

  const handleCardClick = (card: ForecastCardDef) => {
    // Проверяем доступ к функции
    const featureId = card.featureId || card.id;
    const requiredTier = getRequiredTier(featureId);
    const userLevel = TIER_ORDER[userTier as keyof typeof TIER_ORDER] ?? 0;
    const requiredLevel = TIER_ORDER[requiredTier as keyof typeof TIER_ORDER] ?? 0;
    
    if (userLevel < requiredLevel) {
      // Нет доступа — показываем UpgradePrompt
      setPendingFeatureId(featureId);
      setPendingRequiredTier(requiredTier);
      setShowUpgradePrompt(true);
      return;
    }
    
    // Доступ есть — выполняем действие
    if (card.navigateTo) {
      navigate(card.navigateTo);
      return;
    }
    fetchForecast(card.id);
  };

  const handleBackToGrid = () => {
    setActiveForecastId(null);
    setForecastData(null);
    setError(null);
    setShowCustomForm(false);
    setCustomResult(null);
    setCustomError(null);
    setLoading(null);
    setIsCached(false);
  };

  const handleCustomResult = (data: { text: string; question: string }) => {
    setCustomResult(data);
    setCustomError(null);
  };

  const handleCustomError = (message: string) => {
    setCustomError(message);
    setCustomResult(null);
  };

  // ============================================================
  // Render: forecast content
  // ============================================================

  const renderForecastContent = () => {
    if (!forecastData || !activeForecastId) return null;

    // Find card definition
    let cardDef: ForecastCardDef | undefined;
    for (const cat of FORECAST_CATEGORIES) {
      cardDef = cat.forecasts.find(f => f.id === activeForecastId);
      if (cardDef) break;
    }
    const icon = cardDef?.icon || '🔮';

    if (forecastData.type && forecastData.title) {
      return (
        <ForecastCard
          type={forecastData.type}
          title={forecastData.title}
          icon={icon}
          rating={forecastData.rating}
          tone={forecastData.tone}
          summary={forecastData.summary || ''}
          aiInterpretation={forecastData.aiInterpretation}
          recommendations={forecastData.recommendations}
          warnings={forecastData.warnings}
          generatedAt={forecastData.generatedAt}
          cached={forecastData.cached}
        />
      );
    }

    return (
      <div style={cardStyle}>
        <div
          style={{ marginBottom: '20px' }}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(renderMarkdown(forecastData.forecast || forecastData.aiInterpretation || forecastData.summary || forecastData.answer || '')),
          }}
        />

        {forecastData.days && forecastData.days.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h3 style={{ color: '#d4af37', fontSize: '16px', fontFamily: 'var(--font-display)', fontStyle: 'italic', marginBottom: '12px' }}>
              По дням
            </h3>
            {forecastData.days.map((day, i) => (
              <div key={i} style={{ marginBottom: '12px', padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                <strong style={{ color: '#d4af37', fontSize: '13px' }}>{day.date}</strong>
                <p style={{ color: '#b0b0b0', fontSize: '14px', margin: '4px 0 0' }}>{day.summary}</p>
              </div>
            ))}
          </div>
        )}

        {forecastData.highlights && forecastData.highlights.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h3 style={{ color: '#d4af37', fontSize: '16px', fontFamily: 'var(--font-display)', fontStyle: 'italic', marginBottom: '12px' }}>
              Ключевые события
            </h3>
            {forecastData.highlights.map((h, i) => (
              <div key={i} style={{ marginBottom: '8px', color: '#b0b0b0', fontSize: '14px' }}>✦ {h}</div>
            ))}
          </div>
        )}

        {forecastData.recommendations && forecastData.recommendations.length > 0 && (
          <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(34, 139, 34, 0.1)', borderRadius: '12px', border: '1px solid rgba(34, 139, 34, 0.3)' }}>
            <h3 style={{ color: '#44BB44', fontSize: '14px', fontFamily: 'var(--font-sans)', marginBottom: '8px' }}>
              ✅ Рекомендации
            </h3>
            {forecastData.recommendations.map((r, i) => (
              <div key={i} style={{ marginBottom: '6px', color: '#b0b0b0', fontSize: '14px' }}>• {r}</div>
            ))}
          </div>
        )}

        {forecastData.warnings && forecastData.warnings.length > 0 && (
          <div style={{ marginTop: '12px', padding: '16px', background: 'rgba(255, 68, 68, 0.1)', borderRadius: '12px', border: '1px solid rgba(255, 68, 68, 0.3)' }}>
            <h3 style={{ color: '#FF4444', fontSize: '14px', fontFamily: 'var(--font-sans)', marginBottom: '8px' }}>
              ⚠️ Предостережения
            </h3>
            {forecastData.warnings.map((w, i) => (
              <div key={i} style={{ marginBottom: '6px', color: '#b0b0b0', fontSize: '14px' }}>• {w}</div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ============================================================
  // Render
  // ============================================================

  return (
    <div style={pageStyle}>
      <div style={goldAccentLine} />
      <style>{`@keyframes goldShimmer { 0% { background-position: 0% center; } 50% { background-position: 100% center; } 100% { background-position: 0% center; } }`}</style>
      <h1 style={titleStyle}>Прогнозы</h1>

      <p style={{ ...subtitleStyle, fontSize: isMobile ? '14px' : '16px' }}>
        Персонализированные прогнозы на основе вашей натальной карты
      </p>

      {/* Location banner + wizard */}
      {locationLoaded && !activeForecastId && (
        <>
          <LocationBanner
            currentCity={currentCity}
            onUpdateClick={() => setShowLocationWizard(true)}
          />
          {showLocationWizard && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '20px',
            }}>
              <LocationWizard
                onComplete={(loc) => {
                  setCurrentCity(loc.city);
                  setShowLocationWizard(false);
                }}
                onCancel={() => setShowLocationWizard(false)}
              />
            </div>
          )}
        </>
      )}

      {/* Back button when viewing a result */}
      {activeForecastId && (
        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={handleBackToGrid}
            style={backBtnStyle}
          >
            ← Все прогнозы
          </button>
        </div>
      )}

      {/* Card grid — shown when no forecast is active */}
      {!activeForecastId && !showCustomForm && (
        <div>
          {FORECAST_CATEGORIES.map((category) => {
            const isCollapsed = collapsedCategories[category.id] || false;
            return (
              <div key={category.id} style={categorySectionStyle}>
                {/* Category header */}
                <div
                  className="forecast-category-header"
                  style={{ borderColor: `${category.color}33` }}
                  onClick={() => toggleCategory(category.id)}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = `${category.color}66`;
                    (e.currentTarget as HTMLDivElement).style.background = 'rgba(13, 20, 40, 0.95)';
                    (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                    (e.currentTarget as HTMLDivElement).style.boxShadow = '0 12px 32px rgba(212, 175, 55, 0.12), inset 0 0 40px rgba(13, 20, 40, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(212, 175, 55, 0.12)';
                    (e.currentTarget as HTMLDivElement).style.background = 'rgba(13, 20, 40, 0.85)';
                    (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                    (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)';
                  }}
                >
                  <div style={categoryIconStyle(category.color)}>
                    {category.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <span className="forecast-category-title">{category.title}</span>
                    <span className="forecast-category-desc">{category.description}</span>
                  </div>
                  <div style={{ color: '#b0b0b0', fontSize: '20px', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                    ▼
                  </div>
                </div>

                {/* Category cards grid */}
                {!isCollapsed && (
                  <div style={gridContainerStyle}>
                    {category.forecasts.map((card) => {
                      const isLoading = loading === card.id;
                      return (
                        <div
                          key={card.id}
                          onClick={() => handleCardClick(card)}
                          style={{
                            background: isLoading ? 'rgba(212, 175, 55, 0.2)' : 'rgba(13, 20, 40, 0.85)',
                            backdropFilter: 'blur(20px)',
                            border: isLoading ? '2px solid #d4af37' : '1px solid rgba(212, 175, 55, 0.12)',
                            borderRadius: '24px',
                            boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
                            padding: isMobile ? '16px' : '20px',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            opacity: isLoading ? 0.8 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if (!isLoading) {
                              (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(212, 175, 55, 0.4)';
                              (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-4px)';
                              (e.currentTarget as HTMLDivElement).style.boxShadow = '0 12px 48px rgba(212, 175, 55, 0.2), inset 0 0 40px rgba(13, 20, 40, 0.5)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isLoading) {
                              (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(212, 175, 55, 0.12)';
                              (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                              (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)';
                            }
                          }}
                        >
                          <div style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            background: card.gradient,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '24px',
                            marginBottom: '12px',
                            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
                          }}>
                            {card.icon}
                          </div>
                          <h3 style={{
                            color: '#f0f0f0',
                            fontSize: isMobile ? '14px' : '16px',
                            fontFamily: 'var(--font-display)',
                            fontStyle: 'italic',
                            marginBottom: '6px',
                          }}>
                            {card.title}
                            {isLoading && ' ⏳'}
                          </h3>
                          
                          {/* Access Badge */}
                          {!loadingTier && (
                            <div className="mb-2" style={{ minHeight: '24px' }}>
                              <AccessBadge
                                featureId={card.featureId || card.id}
                                userTier={userTier}
                                requiredTier={getRequiredTier(card.featureId || card.id)}
                                size="sm"
                              />
                            </div>
                          )}
                          
                          <p style={{
                            color: '#b0b0b0',
                            fontSize: '13px',
                            lineHeight: 1.5,
                            fontFamily: 'var(--font-sans)',
                          }}>
                            {card.description}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Custom forecast form */}
      {showCustomForm && (
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <CustomForecastForm
            onResult={handleCustomResult}
            onError={handleCustomError}
          />

          {customError && (
            <div style={{
              padding: '16px',
              background: 'rgba(255, 82, 82, 0.1)',
              border: '1px solid rgba(255, 82, 82, 0.3)',
              borderRadius: '12px',
              color: '#FF5252',
              fontSize: '14px',
              marginBottom: '16px',
              fontFamily: 'var(--font-sans)',
            }}>
              {customError}
            </div>
          )}

          {customResult && (
            <div style={{
              background: 'rgba(13, 20, 40, 0.85)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(212, 175, 55, 0.12)',
              borderRadius: '24px',
              boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
              padding: '28px',
              marginBottom: '20px',
            }}>
              <h3 style={{
                color: '#d4af37',
                fontSize: '16px',
                fontFamily: 'var(--font-display)',
                fontStyle: 'italic',
                margin: '0 0 8px',
              }}>
                Ваш вопрос: &laquo;{customResult.question}&raquo;
              </h3>
              <div style={{
                color: '#e0e0e0',
                fontSize: '15px',
                lineHeight: 1.8,
                fontFamily: 'var(--font-sans)',
                whiteSpace: 'pre-wrap',
              }}>
                {customResult.text}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading spinner */}
      {activeForecastId && loading === activeForecastId && !showCustomForm && (
        <AnimatedLoadingState />
      )}

      {/* Error state */}
      {activeForecastId && error && !showCustomForm && (
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <p style={{ color: '#FF5252', fontSize: '14px' }}>{error}</p>
          <button
            onClick={() => activeForecastId && fetchForecast(activeForecastId)}
            style={{
              background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
              border: 'none',
              padding: '10px 24px',
              borderRadius: '25px',
              color: '#0a0814',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              marginTop: '12px',
            }}
          >
            Попробовать снова
          </button>
        </div>
      )}

      {/* Forecast result */}
      {activeForecastId && forecastData && !showCustomForm && renderForecastContent()}

      {/* Cache indicator — показывается под результатом */}
      {activeForecastId && forecastData && isCached && !showCustomForm && (
        <div style={{ textAlign: 'center', marginTop: '8px', marginBottom: '16px' }}>
          <CacheIndicator status="fresh" />
        </div>
      )}

      {/* Other forecasts — horizontal scrollable row under result */}
      {activeForecastId && forecastData && !showCustomForm && (
        <div style={{ marginTop: '32px' }}>
          <h3 style={{ color: '#d4af37', fontSize: '18px', fontFamily: 'var(--font-display)', fontStyle: 'italic', marginBottom: '16px' }}>
            Другие прогнозы
          </h3>
          <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '12px' }}>
            {FORECAST_CATEGORIES.flatMap(cat => cat.forecasts)
              .filter(f => f.id !== activeForecastId)
              .map((card) => {
                const isLoading = loading === card.id;
                return (
                  <div
                    key={card.id}
                    onClick={() => fetchForecast(card.id)}
                    style={{
                      minWidth: '200px',
                      maxWidth: '220px',
                      background: isLoading ? 'rgba(212, 175, 55, 0.2)' : 'rgba(13, 20, 40, 0.85)',
                      backdropFilter: 'blur(20px)',
                      border: isLoading ? '2px solid #d4af37' : '1px solid rgba(212, 175, 55, 0.12)',
                      borderRadius: '24px',
                      boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
                      padding: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: isLoading ? 0.8 : 1,
                      flexShrink: 0,
                    }}
                  >
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>{card.icon}</div>
                    <h4 style={{ color: '#f0f0f0', fontSize: '14px', fontFamily: 'var(--font-display)', fontStyle: 'italic', marginBottom: '4px' }}>
                      {card.title}
                    </h4>
                    <p style={{ color: '#b0b0b0', fontSize: '12px', lineHeight: 1.4, fontFamily: 'var(--font-sans)' }}>
                      {card.description}
                    </p>
                  </div>
                );
              })}
          </div>
        </div>
      )}
      
      {/* Upgrade Prompt Modal */}
      {showUpgradePrompt && pendingFeatureId && (
        <UpgradePrompt
          featureId={pendingFeatureId}
          userTier={userTier}
          requiredTier={pendingRequiredTier}
          onClose={() => {
            setShowUpgradePrompt(false);
            setPendingFeatureId(null);
            setPendingRequiredTier('free');
          }}
        />
      )}
    </div>
  );
};

export default ForecastsPage;
